import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../hooks/useAuth";
import { logService } from "../utils/logService";

const fields = [
  { name: "Agent ID", required: true },
  { name: "Agent Name", required: true },
  {
    name: "Agent Type",
    required: true,
    type: "select",
    options: [
      "Financial Agent",
      "Conversational Agent",
      "Recommendation Agent",
      "Task Automation Agent",
      "Knowledge Agent",
      "Security Agent",
      "Research Agent",
      "Trading Agent",
    ],
  },
  { name: "Version", required: false },
  { name: "Organization", required: false },
  { name: "Agent Description", required: true, type: "textarea" },
  { name: "Intent Scope", required: true },
  { name: "Language Models", required: false },
  { name: "Guardrails", required: false },
];

function RegisterAgent() {
  const [form, setForm] = useState({});
  const navigate = useNavigate();
  const { userRole } = useAuth();
  console.log(auth.currentUser);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate("/signin");
      } else if (userRole === "Moderator") {
        // Moderators cannot register agents
        navigate("/agents");
        toast.error("Moderators cannot register agents");
      }
    });
    return () => unsubscribe();
  }, [navigate, userRole]);

  const handleChange = (e, fieldName) => {
    setForm({ ...form, [fieldName]: e.target.value });
  };

  const validateForm = () => {
    const requiredFields = fields.filter((field) => field.required);
    const missingFields = requiredFields.filter(
      (field) => !form[field.name]?.trim()
    );

    if (missingFields.length > 0) {
      const fieldNames = missingFields.map((field) => field.name).join(", ");
      toast.error(`Please fill all required fields`);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("You must be signed in to register an agent.");
        return;
      }

      // Prepare agent data
      const agentData = {
        ...form,
        userId: user.uid,
        created_by_name: user.displayName || user.email || "Unknown User",
        "Verification Status": "Pending", // Always set to Pending by default
        "Creation Date": new Date().toLocaleDateString(), // Add current date
      };

      const docRef = await addDoc(collection(db, "agents"), agentData);

      // Log the agent creation
      await logService.createLog(
        logService.actions.AGENT_CREATED,
        {
          agentId: docRef.id,
          agentName: agentData["Agent Name"] || "Unnamed Agent",
          agentType: agentData["Agent Type"] || "Unknown Type",
          verificationStatus: agentData["Verification Status"] || "Pending",
        },
        {
          uid: user?.uid,
          email: user?.email,
          displayName: user?.displayName,
        }
      );

      toast.success(
        "Agent submitted successfully! Verifier will review and update the verification status."
      );

      // Reset form after successful submission
      setForm({});
      localStorage.removeItem("agentForm");

      // Redirect to agent list page
      setTimeout(() => {
        navigate("/agents");
      }, 1500);
    } catch (e) {
      toast.error("Error in saving agent");
    }
  };

  React.useEffect(() => {
    localStorage.setItem("agentForm", JSON.stringify(form));
  }, [form]);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-valia-primary mb-2">
              Register New Agent
            </h1>
            <p className="text-valia-gray-600 text-sm sm:text-base">
              Fill in the details to register your AI agent
            </p>
          </div>
          <button
            onClick={() => navigate("/agents")}
            className="valia-btn-secondary valia-btn-sm flex items-center gap-2 self-start sm:self-auto"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Agents
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="bg-valia-background-secondary rounded-valia-lg">
        <form
          onSubmit={(e) => e.preventDefault()}
          className="mx-auto space-y-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {fields.map((field, index) => (
              <div key={index} className="space-y-2">
                <label
                  className={
                    field.required ? "valia-label-required" : "valia-label"
                  }
                >
                  {field.name}
                </label>
                {field.type === "select" ? (
                  <select
                    className="valia-input"
                    value={form[field.name] || ""}
                    onChange={(e) => handleChange(e, field.name)}
                  >
                    <option value="">Select {field.name.toLowerCase()}</option>
                    {field.options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : field.type === "textarea" ? (
                  <textarea
                    className="valia-input resize-none"
                    value={form[field.name] || ""}
                    onChange={(e) => handleChange(e, field.name)}
                    placeholder={`Enter ${field.name.toLowerCase()}`}
                    rows={2}
                  />
                ) : (
                  <input
                    type="text"
                    className="valia-input"
                    value={form[field.name] || ""}
                    onChange={(e) => handleChange(e, field.name)}
                    placeholder={`Enter ${field.name.toLowerCase()}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate("/agents")}
              className="valia-btn-secondary valia-btn-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="valia-btn-primary valia-btn-sm"
            >
              Register Agent
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RegisterAgent;
